import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '@/api/auth';
import { UserInfo, LoginParams, RegisterParams, ResetPasswordParams } from '@/types/auth';
import { useStudioStore } from '@/store/useStudioStore';
import { AUTH_TOKEN_KEY, USER_STORAGE_KEY, clearStoredAuthTokenSources } from '@/lib/authStorage';

/**
 * 身份验证相关的存储键名
 */
/** Token 在 LocalStorage 中的键名 */
export { AUTH_TOKEN_KEY };

/** 写入落地页跨子域共享 cookie，让两个落地页都能感知登录状态 */
function setLpTokenCookie(token: string) {
  const domain = window.location.hostname.includes('coveauto.ai')
    ? '.coveauto.ai'
    : '.shijieboya.cn';
  document.cookie =
    'lp_token=' +
    encodeURIComponent(token) +
    '; path=/; domain=' +
    domain +
    '; max-age=2592000; SameSite=Lax';
}
/** 用户信息在 LocalStorage 中的键名 */
export const USER_INFO_KEY = 'user_info';
/** Zustand 持久化存储在 LocalStorage 中的键名 */
export { USER_STORAGE_KEY };

/**
 * UserState - 用户状态接口定义
 * 维护当前登录用户的个人信息、认证令牌以及登录状态。
 */
interface UserState {
  // --- 状态数据 ---
  /** 当前登录的用户详细信息 */
  user: UserInfo | null;
  /** 身份验证令牌 (JWT) */
  token: string | null;
  /** 标志位：用户是否已通过身份验证 */
  isAuthenticated: boolean;
  /** 标志位：是否正在处理登录、注册等异步请求 */
  isLoading: boolean;
  /** 登录方式：邮箱注册/登录 or OAuth */
  loginSource: 'email' | 'oauth' | null;

  // --- Actions ---
  /**
   * 执行用户登录
   * @param params - 登录参数 (email, password)
   */
  login: (params: LoginParams) => Promise<void>;
  /**
   * 执行用户注册
   * @param params - 注册参数
   */
  register: (params: RegisterParams) => Promise<void>;
  /**
   * 重置用户密码
   * @param params - 重置密码需要的邮箱或验证信息
   */
  resetPassword: (params: ResetPasswordParams) => Promise<void>;
  /**
   * OAuth 登录：直接用 token 和 userId 设置登录态
   */
  loginWithToken: (params: { userId: number; token: string }) => void;
  /**
   * 拉取真实用户信息并更新 store（静默，失败不弹 toast）
   */
  fetchUserInfo: () => Promise<void>;
  /**
   * 退出登录
   * 调用登出接口并清除本地存储的所有认证信息。
   */
  logout: () => Promise<void>;
  /**
   * 纯前端清除认证状态 (不调用接口)
   */
  clearAuth: () => void;
  /**
   * 手动更新本地的用户个人资料
   * @param data - 需要更新的用户属性片段
   */
  updateProfile: (data: Partial<UserInfo>) => void;
}

/**
 * useUserStore - 用户与认证状态中心 (Zustand)
 * 使用 persist 中间件确保令牌和基本用户信息在页面刷新后依然有效。
 */
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      // --- 初始状态 ---
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      loginSource: null,

      /**
       * 登录逻辑实现
       */
      login: async (params) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login(params);
          // 同步存储到原生 localStorage 供非 React 环境或拦截器使用
          localStorage.setItem(AUTH_TOKEN_KEY, response.token);
          setLpTokenCookie(response.token);
          // 更新 Store 状态
          set({
            user: response,
            token: response.token,
            isAuthenticated: true,
            loginSource: 'email',
          });
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * 注册逻辑实现
       */
      register: async (params) => {
        set({ isLoading: true });
        try {
          const response = await authApi.register({
            ...params,
            description: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });
          localStorage.setItem(AUTH_TOKEN_KEY, response.token);
          setLpTokenCookie(response.token);
          set({
            user: response,
            token: response.token,
            isAuthenticated: true,
            loginSource: 'email',
          });
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * 重置密码逻辑实现
       */
      resetPassword: async (params) => {
        set({ isLoading: true });
        try {
          await authApi.resetPassword(params);
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * OAuth 登录逻辑实现
       */
      loginWithToken: ({ userId, token }) => {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        setLpTokenCookie(token);
        set({
          user: { id: userId, token, name: '', email: '', phone: '', icon: '', createTime: '' },
          token,
          isAuthenticated: true,
          loginSource: 'oauth',
        });
      },

      /**
       * 拉取真实用户信息（静默，失败不弹 toast）
       */
      fetchUserInfo: async () => {
        try {
          const info = await authApi.getUserInfo({ silent: true });
          get().updateProfile(info);
        } catch {
          // 静默失败，不影响登录状态
        }
      },

      /**
       * 退出登录逻辑实现
       */
      logout: async () => {
        const { user } = get();
        if (user?.email) {
          try {
            // 通知后端销毁会话
            await authApi.logout({ email: user.email });
          } catch (e) {
            console.warn('Logout API failed, clearing local state anyway', e);
          }
        }
        // 无论接口是否成功，都必须清除本地状态
        get().clearAuth();
      },

      /**
       * 清除本地所有认证相关的持久化数据
       */
      clearAuth: () => {
        localStorage.removeItem(USER_INFO_KEY);
        // Clear every token source before route guards can recover a stale login.
        clearStoredAuthTokenSources();
        useStudioStore.getState().clearStudioState();
        set({ user: null, token: null, isAuthenticated: false, loginSource: null });
      },

      /**
       * 局部更新用户信息（如修改头像、昵称后同步）
       */
      updateProfile: (data) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...data } : null,
        })),
    }),
    {
      /** 持久化名称 */
      name: USER_STORAGE_KEY,
      /** 选择性持久化字段 */
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        loginSource: state.loginSource,
      }),
    },
  ),
);
